import React from 'react';
import { Box, Divider } from '@mui/material';
import VeraPromptContextGuide from '../VeraPromptContextGuide';
import { getVeraPromptContextGuide } from '../veraPromptContextGuides';
import { StyledButton } from '../../styles/StyledButtons';
import StyledFieldDescription from '../../styles/StyledFieldDescription';
import { StyledLabel } from '../../styles/styles';
import {
  MONOSPACE_TEXTAREA_STYLE,
  PRODUCTION_REFERENCE_SX,
} from '../VeraAdminChatAssistPromptSandbox/adminAssistPromptStyles';

interface InvestorIntentPromptTabProps {
  intentText: string;
  intentProductionText: string;
  globalGuideOptions: { googleSearchEnabled: boolean; googleSearchLinksEnabled: boolean };
  onIntentChange: (value: string) => void;
  onResetToProduction: () => void;
}

export default function InvestorIntentPromptTab({
  intentText,
  intentProductionText,
  globalGuideOptions,
  onIntentChange,
  onResetToProduction,
}: InvestorIntentPromptTabProps) {
  return (
    <>
      <VeraPromptContextGuide
        markdown={getVeraPromptContextGuide('global_intent', globalGuideOptions)}
      />
      <StyledFieldDescription stacked>
        This classifier runs <strong>in parallel</strong> with the main Vera AI call — not before it
        — so it adds no latency to responses. It detects <code>ADVICE</code>, <code>LOCATION</code>,
        or <code>ALLOWED</code> intent and the result is used to apply financial advice guardrails
        or load Google Maps context into the concurrent main call. Keep this prompt short and
        precise: the model outputs exactly one word.
      </StyledFieldDescription>

      <Box sx={{ mb: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <StyledLabel stacked>Intent Classifier Prompt</StyledLabel>
          <StyledButton
            size="small"
            variant="outlined"
            onClick={onResetToProduction}
            disabled={intentText.trim() === intentProductionText.trim()}
          >
            Reset to coded version
          </StyledButton>
        </Box>
        <StyledFieldDescription stacked>
          Plain text only — the model outputs exactly one word: <code>ADVICE</code>,{' '}
          <code>LOCATION</code>, or <code>ALLOWED</code>.
        </StyledFieldDescription>
        <textarea
          value={intentText}
          onChange={(event) => onIntentChange(event.target.value)}
          rows={12}
          style={{ ...MONOSPACE_TEXTAREA_STYLE, marginTop: 8 }}
        />
      </Box>

      {intentProductionText && (
        <Box sx={{ mb: 3 }}>
          <StyledLabel stacked>Current coded version (read-only reference)</StyledLabel>
          <Box component="pre" sx={PRODUCTION_REFERENCE_SX}>
            {intentProductionText}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />
    </>
  );
}
